import React from "react";
import Reveal from "./Reveal";
import { AUDIENCES } from "../lib/audiences";

/**
 * Promoted Audiences section — full-bleed hero treatment with bold typography,
 * gold accent rails, and a left-aligned editorial layout.
 */
export default function Audiences() {
  return (
    <section
      id="audiences"
      className="relative py-10 md:py-14 border-t hairline overflow-hidden"
      data-testid="audiences-section"
    >
      {/* Soft pattern */}
      <div className="absolute inset-0 pattern-diagonal opacity-50 pointer-events-none" />

      <div className="container-edge relative">
        <Reveal>
          <p className="section-number">03 — WHO IT'S FOR</p>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <h2
                className="font-display text-[40px] sm:text-[52px] lg:text-[68px] leading-[0.95] tracking-tight"
                style={{ fontWeight: 500 }}
              >
                Four audiences.
                <br />
                <em className="italic grad-brand-text" style={{ fontWeight: 400 }}>
                  One ecosystem.
                </em>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={140}>
              <p
                className="text-[17px] md:text-[19px] leading-relaxed"
                style={{ color: "var(--ink-secondary)" }}
              >
                The same Practitioner Method, scoped four ways. Each audience gets a tailored
                programme — and every learner shares one global community, one Innovation Arena,
                one founder pipeline.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Cards — 2x2 with bold ghost numerals and vibrant accent rails */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {AUDIENCES.map((a, i) => (
            <Reveal key={a.id} delay={i * 100}>
              <article
                className="group relative overflow-hidden bg-white border hairline-strong p-8 lg:p-12 h-full"
                style={{ borderRadius: 4 }}
                data-testid={`audience-card-${a.id}`}
              >
                {/* Top gradient rail */}
                <div
                  className="absolute top-0 left-0 right-0 h-[3px]"
                  style={{ background: "linear-gradient(90deg, #4F46E5 0%, #7C3AED 60%, #C9A961 100%)" }}
                />

                {/* Ghost numeral */}
                <span
                  className="ghost-numeral"
                  style={{ top: -50, right: -20, fontSize: 280 }}
                  aria-hidden="true"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>

                <div className="relative">
                  <div className="flex items-center gap-3">
                    <span className="mono-label-gold">{a.code}</span>
                    <span
                      className="inline-block w-1 h-1 rounded-full"
                      style={{ background: "var(--accent-gold)" }}
                    />
                    <span className="font-mono text-[11px]" style={{ color: "var(--ink-tertiary)", letterSpacing: "0.14em" }}>
                      {a.tag.toUpperCase()}
                    </span>
                  </div>

                  <h3
                    className="mt-6 font-display text-[40px] md:text-[48px] tracking-tight leading-[1]"
                    style={{ fontWeight: 500 }}
                  >
                    {a.title}
                  </h3>

                  <p
                    className="mt-4 italic text-[16px] max-w-[460px]"
                    style={{ color: "var(--ink-secondary)" }}
                  >
                    {a.forWhom}
                  </p>

                  <ul className="mt-8 space-y-2.5">
                    {a.bullets.map((b) => (
                      <li
                        key={b}
                        className="text-[14.5px] leading-relaxed pl-5 relative"
                        style={{ color: "var(--ink-secondary)" }}
                      >
                        <span
                          className="absolute left-0 top-[10px] w-3 h-px"
                          style={{ background: "var(--accent-gold)" }}
                        />
                        {b}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#enquiry"
                    onClick={() => {
                      try {
                        window.dispatchEvent(
                          new CustomEvent("enquiry:prefill", { detail: { audience: a.id } })
                        );
                      } catch (_) {}
                    }}
                    className="mt-10 inline-flex items-center gap-2 text-[14.5px] font-medium text-ink group/cta"
                    data-testid={`audience-cta-${a.id}`}
                  >
                    <span className="link-underline">{a.cta}</span>
                    <span className="transition-transform group-hover/cta:translate-x-1">→</span>
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
